import React, { useState, useEffect } from 'react';
import { Render } from '@measured/puck';
import '@measured/puck/puck.css';
import { puckConfig } from '../puck/puckConfig';

// Standalone theme toggle for landing pages
function StandaloneThemeToggle({ theme, setTheme }) {
    const isDark = theme === 'dark';
    return (
        <button
            onClick={() => setTheme(isDark ? 'light' : 'dark')}
            className="fixed top-4 right-4 z-9999 p-3 bg-white/90 dark:bg-slate-800/90 backdrop-blur-xs border border-slate-200 dark:border-slate-700 rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
            <span className="text-slate-700 dark:text-slate-300">
                {isDark ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                    </svg>
                ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                    </svg>
                )}
            </span>
        </button>
    );
}

export function LandingPageRenderer({ campaign }) {
  // Theme state for landing pages
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'light' || saved === 'dark') return saved;
      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) return 'dark';
    } catch {}
    return 'light';
  });

  // Sync theme with localStorage and apply to document
  useEffect(() => {
    localStorage.setItem('theme', theme);
    // Apply dark mode to html element for global CSS
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [theme]);

  // If no landing page data exists, show a default message
  if (!campaign.landingPage || !campaign.landingPage.content || campaign.landingPage.content.length === 0) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center font-sf-pro">
        <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-100 mb-4">Landing Page nicht verfügbar</h1>
          <p className="text-slate-600 dark:text-slate-300">Diese Kampagne hat noch keine Landing Page erstellt.</p>
        </div>
      </div>
    );
  }

  // Helper function to replace campaign-id placeholder with actual campaign ID
  const replaceCampaignIdPlaceholder = (obj) => {
    if (typeof obj === 'string') {
      return obj.replace(/\/apply\/campaign-id/g, `/apply/${campaign.id}`);
    }
    if (Array.isArray(obj)) {
      return obj.map(replaceCampaignIdPlaceholder);
    }
    if (obj && typeof obj === 'object') {
      const newObj = {};
      for (const [key, value] of Object.entries(obj)) {
        newObj[key] = replaceCampaignIdPlaceholder(value);
      }
      return newObj;
    }
    return obj;
  };

  // Update all components to replace campaign-id placeholder with actual campaign ID
  const updatedLandingPageData = {
    ...campaign.landingPage,
    content: replaceCampaignIdPlaceholder(campaign.landingPage.content)
  };

  return (
    <div className="min-h-screen landing-page font-sf-pro bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100">
      <StandaloneThemeToggle theme={theme} setTheme={setTheme} />
      <Render 
        config={puckConfig} 
        data={updatedLandingPageData} 
      />
    </div>
  );
}
